import { set, child, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'

import * as firebaseConnection from '../firebaseConnection'

const webinarGlobalState = proxy<{
  isInitialized: boolean
  isWebinarLive: boolean
  ended: Date | null
}>({
  isInitialized: false,
  isWebinarLive: false,
  ended: null,
})

firebaseConnection.runWhenReady((connection) => {
  webinarGlobalState.isInitialized = true

  // Adds a Firebase listener to track the live state of the webinar.
  onValue(child(connection.room, 'isWebinarLive'), (snapshot) => {
    webinarGlobalState.isWebinarLive = snapshot?.val() ?? false
  })

  // Adds a Firebase listener to receive "ended" state changes.
  onValue(child(connection.room, 'ended'), (snapshot) => {
    const ended = snapshot.val() ?? undefined
    if (ended) {
      webinarGlobalState.ended = new Date(ended)
    } else {
      webinarGlobalState.ended = null
    }
  })
})

/**
 * Sets the webinar live state.
 * @param state - The webinar live state.
 */
const setIsWebinarLive = async (state: boolean) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, 'isWebinarLive'), state).catch(
    (err: Error) => {
      connection.logError(
        'SET_IS_WEBINAR_LIVE',
        'Failed to set the webinar live status',
        err
      )
    }
  )
}

/**
 * Sets the webinar state to ended.
 */
const setEnded = async () => {
  const connection = await firebaseConnection.get()

  const date = new Date()

  return set(child(connection.room, 'ended'), date.toISOString()).catch(
    (err: Error) => {
      connection.logError('SET_ENDED_AT', 'Failed to set ended at', err)
    }
  )
}

const useHasEndedState = () => useSnapshot(webinarGlobalState).ended

const useIsInitializedState = () =>
  useSnapshot(webinarGlobalState).isInitialized

const useIsWebinarLiveState = () =>
  useSnapshot(webinarGlobalState).isWebinarLive

const isWebinarLiveState = () => webinarGlobalState.isWebinarLive

export {
  webinarGlobalState,
  useHasEndedState,
  useIsInitializedState,
  useIsWebinarLiveState,
  setIsWebinarLive,
  isWebinarLiveState,
  setEnded,
}
